// src/pages/mushroom-strain-edit-page.tsx

import React from "react";
import { useHistory, useParams } from "react-router-dom";
import { Box, Button, TextField, Divider } from "@mui/material";
import { DateTime } from "luxon";
import Page from "../components/page";
import LoadingBackdrop from "../components/loading-backdrop";

interface MushroomStrain {
  id: number;
  name: string;
  species: string | null;
  notes: string | null;
  archivedAt: string | null;

  daysAgarPlate: number | null;
  daysLiquidCulture: number | null;
  daysGrainSpawn: number | null;
  daysBulkSpawn: number | null;
  daysFruitingBlockColonization: number | null;
  daysCasing: number | null;
  daysFruitingToFirstHarvest: number | null;
  harvestWindowDays: number | null;
  daysFirstToSecondFlush: number | null;
  daysSecondToThirdFlush: number | null;
}

const mushroomStrainsStorageKey = "mushroomStrains";

type RouteParams = { id: string };

function loadStrains(): MushroomStrain[] {
  const raw = window.localStorage.getItem(mushroomStrainsStorageKey);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as MushroomStrain[]) : [];
  } catch {
    window.localStorage.removeItem(mushroomStrainsStorageKey);
    return [];
  }
}

function saveStrains(next: MushroomStrain[]) {
  window.localStorage.setItem(mushroomStrainsStorageKey, JSON.stringify(next));
}

export default function MushroomStrainEditPage() {
  const history = useHistory();
  const params = useParams<RouteParams>();
  const isNew = !params.id || params.id === "new";

  const strainId = isNew ? null : Number(params.id);

  const numericId = isNew ? null : Number(params.id);

  const [busy, setBusy] = React.useState(false);
  const [allStrains, setAllStrains] = React.useState<MushroomStrain[]>(() =>
    loadStrains(),
  );

  const existing = React.useMemo(() => {
    if (isNew) return null;
    const idNum = Number(params.id);
    return allStrains.find((s) => s.id === idNum) ?? null;
  }, [allStrains, isNew, params.id]);

  // Form state
  const [name, setName] = React.useState(existing?.name ?? "");
  const [species, setSpecies] = React.useState(existing?.species ?? "");
  const [notes, setNotes] = React.useState(existing?.notes ?? "");

  const [daysAgarPlate, setDaysAgarPlate] = React.useState(
    String(existing?.daysAgarPlate ?? 14),
  );
  const [daysLiquidCulture, setDaysLiquidCulture] = React.useState(
    String(existing?.daysLiquidCulture ?? 10),
  );
  const [daysGrainSpawn, setDaysGrainSpawn] = React.useState(
    String(existing?.daysGrainSpawn ?? 14),
  );
  const [daysBulkSpawn, setDaysBulkSpawn] = React.useState(
    String(existing?.daysBulkSpawn ?? 0),
  );
  const [daysFruitingBlockColonization, setDaysFruitingBlockColonization] =
    React.useState(String(existing?.daysFruitingBlockColonization ?? 14));
  const [daysCasing, setDaysCasing] = React.useState(
    String(existing?.daysCasing ?? 0),
  );
  const [daysFruitingToFirstHarvest, setDaysFruitingToFirstHarvest] =
    React.useState(String(existing?.daysFruitingToFirstHarvest ?? 7));
  const [harvestWindowDays, setHarvestWindowDays] = React.useState(
    String(existing?.harvestWindowDays ?? 5),
  );
  const [daysFirstToSecondFlush, setDaysFirstToSecondFlush] = React.useState(
    String(existing?.daysFirstToSecondFlush ?? 7),
  );
  const [daysSecondToThirdFlush, setDaysSecondToThirdFlush] = React.useState(
    String(existing?.daysSecondToThirdFlush ?? 7),
  );

  // If the user refreshes / navigates between ids without full remount
  React.useEffect(() => {
    if (!existing && !isNew) return;

    setName(existing?.name ?? "");
    setSpecies(existing?.species ?? "");
    setNotes(existing?.notes ?? "");

    setDaysAgarPlate(String(existing?.daysAgarPlate ?? 14));
    setDaysLiquidCulture(String(existing?.daysLiquidCulture ?? 10));
    setDaysGrainSpawn(String(existing?.daysGrainSpawn ?? 14));
    setDaysBulkSpawn(String(existing?.daysBulkSpawn ?? 0));
    setDaysFruitingBlockColonization(
      String(existing?.daysFruitingBlockColonization ?? 14),
    );
    setDaysCasing(String(existing?.daysCasing ?? 0));
    setDaysFruitingToFirstHarvest(
      String(existing?.daysFruitingToFirstHarvest ?? 7),
    );
    setHarvestWindowDays(String(existing?.harvestWindowDays ?? 5));
    setDaysFirstToSecondFlush(String(existing?.daysFirstToSecondFlush ?? 7));
    setDaysSecondToThirdFlush(String(existing?.daysSecondToThirdFlush ?? 7));
  }, [existing, isNew]);

  const title = isNew ? "Add Strain" : "Edit Strain";

  const handleSave = () => {
    if (!name.trim()) return;

    setBusy(true);
    try {
      const nowIso = DateTime.local().toISO();

      if (isNew) {
        const maxId = allStrains.reduce((m, s) => Math.max(m, s.id), 0);
        const next: MushroomStrain = {
          id: maxId + 1,
          name: name.trim(),
          species: species.trim() ? species.trim() : null,
          notes: notes.trim() ? notes.trim() : null,
          archivedAt: null,

          daysAgarPlate: Number(daysAgarPlate) || 0,
          daysLiquidCulture: Number(daysLiquidCulture) || 0,
          daysGrainSpawn: Number(daysGrainSpawn) || 0,
          daysBulkSpawn: Number(daysBulkSpawn) || 0,
          daysFruitingBlockColonization:
            Number(daysFruitingBlockColonization) || 0,
          daysCasing: Number(daysCasing) || 0,
          daysFruitingToFirstHarvest: Number(daysFruitingToFirstHarvest) || 0,
          harvestWindowDays: Number(harvestWindowDays) || 0,
          daysFirstToSecondFlush: Number(daysFirstToSecondFlush) || 0,
          daysSecondToThirdFlush: Number(daysSecondToThirdFlush) || 0,
        };

        const updated = [next, ...allStrains];
        setAllStrains(updated);
        saveStrains(updated);
      } else {
        if (!existing) return;

        const updated = allStrains.map((s) =>
          s.id === existing.id
            ? {
                ...s,
                name: name.trim(),
                species: species.trim() ? species.trim() : null,
                notes: notes.trim() ? notes.trim() : null,

                daysAgarPlate: Number(daysAgarPlate) || 0,
                daysLiquidCulture: Number(daysLiquidCulture) || 0,
                daysGrainSpawn: Number(daysGrainSpawn) || 0,
                daysBulkSpawn: Number(daysBulkSpawn) || 0,
                daysFruitingBlockColonization:
                  Number(daysFruitingBlockColonization) || 0,
                daysCasing: Number(daysCasing) || 0,
                daysFruitingToFirstHarvest:
                  Number(daysFruitingToFirstHarvest) || 0,
                harvestWindowDays: Number(harvestWindowDays) || 0,
                daysFirstToSecondFlush: Number(daysFirstToSecondFlush) || 0,
                daysSecondToThirdFlush: Number(daysSecondToThirdFlush) || 0,

                // example if you later want “lastUpdatedAt”: nowIso,
              }
            : s,
        );

        setAllStrains(updated);
        saveStrains(updated);
      }

      history.push("/mushroom/home/strains");
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = () => history.push("/mushroom/home/strains");

  if (!isNew && !existing) {
    return (
      <Page title="Strain Not Found">
        <Box sx={{ mt: 2 }}>
          This strain doesn’t exist.{" "}
          <Button onClick={handleCancel}>Back to Strains</Button>
        </Box>
      </Page>
    );
  }

  return (
    <Page title={title}>
      <Divider sx={{ mb: 2 }} />

      <Box
        sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 700 }}
      >
        <TextField
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
        />
        <TextField
          label="Species"
          value={species}
          onChange={(e) => setSpecies(e.target.value)}
          fullWidth
        />
        <TextField
          label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          fullWidth
          multiline
          minRows={3}
        />

        <Divider />

        <TextField
          label="Agar / Plate (days)"
          type="number"
          value={daysAgarPlate}
          onChange={(e) => setDaysAgarPlate(e.target.value)}
          fullWidth
        />
        <TextField
          label="Liquid Culture (days)"
          type="number"
          value={daysLiquidCulture}
          onChange={(e) => setDaysLiquidCulture(e.target.value)}
          fullWidth
        />
        <TextField
          label="Grain Spawn (days)"
          type="number"
          value={daysGrainSpawn}
          onChange={(e) => setDaysGrainSpawn(e.target.value)}
          fullWidth
        />
        <TextField
          label="Bulk Spawn (days)"
          type="number"
          value={daysBulkSpawn}
          onChange={(e) => setDaysBulkSpawn(e.target.value)}
          fullWidth
        />
        <TextField
          label="Fruiting Block (Colonization) (days)"
          type="number"
          value={daysFruitingBlockColonization}
          onChange={(e) => setDaysFruitingBlockColonization(e.target.value)}
          fullWidth
        />
        <TextField
          label="Casing (days)"
          type="number"
          value={daysCasing}
          onChange={(e) => setDaysCasing(e.target.value)}
          fullWidth
        />
        <TextField
          label="Fruiting → First Harvest (days)"
          type="number"
          value={daysFruitingToFirstHarvest}
          onChange={(e) => setDaysFruitingToFirstHarvest(e.target.value)}
          fullWidth
        />
        <TextField
          label="Harvest Window (days)"
          type="number"
          value={harvestWindowDays}
          onChange={(e) => setHarvestWindowDays(e.target.value)}
          fullWidth
        />
        <TextField
          label="First → Second Flush (days)"
          type="number"
          value={daysFirstToSecondFlush}
          onChange={(e) => setDaysFirstToSecondFlush(e.target.value)}
          fullWidth
        />
        <TextField
          label="Second → Third Flush (days)"
          type="number"
          value={daysSecondToThirdFlush}
          onChange={(e) => setDaysSecondToThirdFlush(e.target.value)}
          fullWidth
        />
      </Box>
      <Divider sx={{ mt: 3 }} />

      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          mt: 2,
          pt: 2,
          pb: 2,
          backgroundColor: "background.paper",
          display: "flex",
          justifyContent: "left",
          gap: 1,
          zIndex: 10,
        }}
      >
        <Button onClick={handleCancel} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={busy || !name.trim()}
        >
          Save
        </Button>
      </Box>

      <LoadingBackdrop open={busy} />
    </Page>
  );
}
